import { styled, InteractiveElement, StyledIconButton } from "@iventis/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useDebounce, useKeyboardEvent } from "@iventis/utilities";
import { TextField, ClickAwayListener } from "@mui/material";

import React, { ChangeEvent, FunctionComponent, useEffect, useRef, useState } from "react";

interface HyperlinkInputProps {
    value: string;
    placeholderValue?: string;
    /** Set debounce to 0 to not have one */
    debounceTime?: number;
    /** If the link being clicked is invalid */
    onClickLinkError: () => void;
    onValueChange: (value: string) => void;
    testId: string;
}

/** Input component which contains a hyperlink, can be edited by clicking the edit button */
export const HyperlinkInput: FunctionComponent<HyperlinkInputProps> = ({ value, testId, placeholderValue, debounceTime = 0, onClickLinkError, onValueChange }) => {
    const { debouncedValue, setValue: setInputValue, value: inputValue } = useDebounce(value, debounceTime);

    const inputElement = useRef<HTMLInputElement>(null);

    const [edit, setEdit] = useState(false);

    /** Updates value with external function and stops editing */
    const updateValue = () => {
        if (debounceTime !== 0) {
            setEdit(false);
        }
        if (value !== debouncedValue) {
            onValueChange(debouncedValue);
        }
    };

    // Press enter will automatically update the value
    useKeyboardEvent(() => updateValue(), edit, "Enter", inputElement?.current);

    // When debounce has finished debounceValue will update
    useEffect(() => {
        updateValue();
    }, [debouncedValue]);

    const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        if (edit) {
            setInputValue(value);
        }
    };

    const handleClick = () => {
        if (value !== "" && value != null) {
            // Ensure that the link has https:// or http:// otherwise it will link to somewhere in our app
            let parsedValue = value;
            if (!parsedValue.startsWith("http://") && !parsedValue.startsWith("https://")) {
                parsedValue = `https://${parsedValue}`;
            }

            // Link inputted might not be able to be opened by the browser if invalid
            try {
                window.open(parsedValue, "_blank").focus();
            } catch (err) {
                onClickLinkError();
            }
        } else {
            setEdit(true);
        }
    };

    // When we click away from the input we want to save the changes
    const handleClickAway = () => {
        if (edit) {
            updateValue();
        }
    };

    // Autofocus on the input when we go to edit it
    useEffect(() => {
        if (edit) {
            const input = inputElement.current.getElementsByTagName("input")[0];
            input.focus();
        }
    }, [edit]);

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Container editing={edit}>
                {!edit && (
                    <>
                        <InteractiveElement className="link-button" onClick={handleClick} />
                    </>
                )}
                <StyledIconButton className="edit-button" onClick={() => setEdit(!edit)} size="small" data-testid="hyperlink-edit-button">
                    <FontAwesomeIcon icon={["fas", edit ? "check" : "edit"]} />
                </StyledIconButton>
                <Input
                    ref={inputElement}
                    placeholder={placeholderValue}
                    value={inputValue}
                    onChange={handleValueChange}
                    disabled={!edit}
                    $editing={edit}
                    onFocus={(event) => event.target.select()}
                    data-testid={`hyperlink-${testId}`}
                />
            </Container>
        </ClickAwayListener>
    );
};

const Container = styled.div<{ editing: boolean }>`
    width: 100%;
    position: relative;

    .edit-button {
        position: absolute;
        right: 0;
        top: 0;
        z-index: 101;
        color: ${(props) => (props.editing ? props.theme.secondaryColors.confirm : props.theme.shades.two)};
    }

    .link-button {
        position: absolute;
        height: 100%;
        width: 100%;
        z-index: 100;
    }
`;

const Input = styled(TextField)<{ $editing: boolean }>`
    width: 100%;
    .Mui-disabled {
        // Overwrite MUI default disabled colour
        -webkit-text-fill-color: ${(props) => props.theme.tertiaryColors.primary} !important;
        text-decoration: underline;
    }

    input {
        // Ensures that the link in the input is not covered by the edit button
        padding-right: 30px !important;
        text-overflow: ellipsis;
        font-weight: ${(props) => (props.$editing ? 400 : 600)};
    }
`;
